import { Box, Typography, Link } from '@mui/material';
import { styled } from '@mui/material/styles';

// Use unified date utils
import { moment } from '../../utils/dateUtils';

const fontFamily = '"Inter", sans-serif';

// Small logo - for screen only, print uses CSS class
const SmallLogoImg = styled('img')(({ theme }) => ({
  width: '100%',
  maxWidth: '200px',
  height: 'auto',
  [theme.breakpoints.down('sm')]: {
    maxWidth: '150px',
  },
}));

// Full size logo - for screen only, print uses CSS class  
const FullLogoImg = styled('img')(({ theme }) => ({
  width: '100%',
  maxWidth: '400px',
  height: 'auto',
  [theme.breakpoints.down('sm')]: {
    maxWidth: '280px',
  },
}));

// Format phone number for display
const formatPhone = (phone) => {
  if (!phone) return null;
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `${cleaned.slice(1, 4)}-${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  if (cleaned.length === 10) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return phone;
};

const ProposalViewAwards = ({ 
  techName, 
  techPhone,
  techEmail,
  proposalId, 
  issueDate,
  installationSchedule = [],
  installDayInfo = null,
  smallLogo = false
}) => (
  <Box sx={{ mb: 4 }}>
    {/* Top Row: Logo left, Info right - responsive */}
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: { xs: 'center', sm: 'space-between' },
        alignItems: { xs: 'center', sm: 'flex-start' },
        gap: { xs: 2, sm: 0 },
        px: 2,
        pt: 2,
        pb: 2,
      }}
    >
      {/* Left: Logo */}
      <Box sx={{ 
        flexShrink: 0,
        textAlign: { xs: 'center', sm: 'left' },
      }}>
        {smallLogo ? (
          <SmallLogoImg src="/images/Airmaxx-Logo-Trans.png" alt="Airmaxx" className="proposal-logo proposal-logo-small" />
        ) : (
          <FullLogoImg src="/images/Airmaxx-Logo-Trans.png" alt="Airmaxx" className="proposal-logo proposal-logo-full" />
        )}
      </Box>

      {/* Right: Contract ID, Comfort Advisor, Cell, Issue Date, Install Date */}
      <Box sx={{ 
        textAlign: { xs: 'center', sm: 'right' },
        minWidth: { sm: '200px' },
      }}>
        {/* Contract ID - First row */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: { xs: 'center', sm: 'flex-end' }, 
          alignItems: { xs: 'center', sm: 'baseline' }, 
          gap: 0.5, 
          mb: 0.5 
        }}>
          <Typography
            sx={{
              fontFamily,
              fontSize: 11,
              color: '#666',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Contract ID:
          </Typography>
          <Typography
            className="contract-id-value"
            sx={{
              fontFamily,
              fontSize: 16,
              fontWeight: 700,
              color: '#0d3c61',
            }}
          >
            {proposalId}
          </Typography>
        </Box>

        {/* Comfort Advisor */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: { xs: 'center', sm: 'flex-end' }, 
          alignItems: { xs: 'center', sm: 'baseline' }, 
          gap: 0.5, 
          mb: 0.5 
        }}>
          <Typography
            sx={{
              fontFamily,
              fontSize: 11,
              color: '#666',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Comfort Advisor:
          </Typography>
          <Typography
            sx={{
              fontFamily,
              fontSize: 14,
              fontWeight: 600,
              color: '#1a1a1a',
            }}
          >
            {techName || 'Airmaxx Team'}
          </Typography>
        </Box>

        {/* Cell Phone */}
        {techPhone && (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: { xs: 'center', sm: 'flex-end' }, 
            alignItems: { xs: 'center', sm: 'baseline' }, 
            gap: 0.5, 
            mb: 0.5 
          }}>
            <Typography
              sx={{
                fontFamily,
                fontSize: 11,
                color: '#666',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Cell:
            </Typography>
            <Link
              href={`tel:${techPhone}`}
              sx={{
                fontFamily,
                fontSize: 14,
                fontWeight: 500,
                color: '#1a1a1a',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {formatPhone(techPhone)}
            </Link>
          </Box>
        )}

        {/* Email - Hidden per user request
        {techEmail && (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: { xs: 'center', sm: 'flex-end' }, 
            alignItems: { xs: 'center', sm: 'baseline' }, 
            gap: 0.5, 
            mb: 0.5 
          }}>
            <Typography
              sx={{
                fontFamily,
                fontSize: 11,
                color: '#666',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Email:
            </Typography>
            <Link
              href={`mailto:${techEmail}`}
              sx={{
                fontFamily,
                fontSize: 14,
                fontWeight: 500,
                color: '#1a1a1a',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              {techEmail}
            </Link>
          </Box>
        )}
        */}

        {/* Issue Date */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: { xs: 'center', sm: 'flex-end' }, 
          alignItems: { xs: 'center', sm: 'baseline' }, 
          gap: 0.5, 
          mb: 0.5 
        }}>
          <Typography
            sx={{
              fontFamily,
              fontSize: 11,
              color: '#666',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Issue Date:
          </Typography>
          <Typography
            sx={{
              fontFamily,
              fontSize: 14,
              fontWeight: 500,
              color: '#1a1a1a',
            }}
          >
            {moment(issueDate).format('MM/DD/YYYY')}
          </Typography>
        </Box>

        {/* Installation Date */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: { xs: 'center', sm: 'flex-end' }, 
          alignItems: { xs: 'center', sm: 'baseline' }, 
          gap: 0.5 
        }}>
          <Typography
            sx={{
              fontFamily,
              fontSize: 11,
              color: '#666',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Install Date:
          </Typography>
          <Typography
            sx={{
              fontFamily,
              fontSize: 14,
              fontWeight: 500,
              color: '#1a1a1a',
              textAlign: { xs: 'center', sm: 'right' },
            }}
          >
            {installationSchedule.length > 0 
              ? installationSchedule.map((s, idx) => (
                  <span key={s.id || idx}>
                    {moment(s.date).utc().format('MM/DD/YYYY')}
                    {s.installationTime && ` ${s.installationTime}`}
                    {installDayInfo && ` (Day ${installDayInfo.day} of ${installDayInfo.total})`}
                    {idx < installationSchedule.length - 1 && ', '}
                  </span>
                ))
              : 'To be scheduled'
            }
          </Typography>
        </Box>
      </Box>
    </Box>
  </Box>
);

export default ProposalViewAwards;
